import React, { useEffect, useRef } from 'react';
import { Typography } from '@pw-tech/omni-ui';
import { Check } from 'lucide-react';
type StepState = 'completed' | 'active' | 'in-progress' | 'upcoming';
interface StepItem {
  description: string;
}
interface StepperComponenetProps {
  steps: StepItem[];
  /** 0-based index of the step the user is currently viewing */
  currentStep: number;
  /** 0-based index of the highest step the user has reached */
  highestReachedStep: number;
  orientation?: 'horizontal' | 'vertical';
  textAlignment?: 'right' | 'bottom';
  onClickStep?: (index: number) => void;
  autoScroll?: boolean;
  /** 0-based indices of steps that should not be clickable even if completed */
}
const stateStyles: Record<StepState, string> = {
  completed: 'bg-[#1b7938] text-white',
  active: 'bg-[#1b2124] text-white font-semibold',
  'in-progress': 'bg-[#E67E22] text-white font-medium',
  upcoming: 'bg-[#b5bbc5] text-white font-medium'
};
const getStepState = (index: number, currentStep: number, highestReachedStep: number): StepState => {
  if (index === currentStep) return 'active';
  if (currentStep < highestReachedStep && index === highestReachedStep) return 'in-progress';
  if (index < highestReachedStep) return 'completed';
  return 'upcoming';
};
const StepperComponenet = ({
  steps,
  currentStep,
  highestReachedStep,
  orientation = 'vertical',
  textAlignment = 'right',
  onClickStep,
  autoScroll = false
}: StepperComponenetProps) => {
  const activeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (autoScroll && activeRef.current) {
      activeRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [currentStep, autoScroll]);
  const isVertical = orientation === 'vertical';
  const isTextBottom = textAlignment === 'bottom';
  return <div className={`flex ${isVertical ? 'flex-col' : 'flex-row items-start'} ${isVertical && isTextBottom ? 'items-center' : ''} whitespace-nowrap`} data-sentry-component="StepperComponenet" data-sentry-source-file="StepperComponenet.tsx">
      {steps.map((step, index) => {
      const state = getStepState(index, currentStep, highestReachedStep);
      const isClickable = state !== 'active' && index <= highestReachedStep;
      const isLast = index === steps.length - 1;
      const stepContent = <>
            {/* Step circle */}
            <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center flex-shrink-0 ${stateStyles[state]}`}>
              {state === 'completed' ? <Check size={14} strokeWidth={3} /> : <span className="text-xs sm:text-sm">{index + 1}</span>}
            </div>

            {/* Step text */}
            <div className={isTextBottom ? 'mt-1.5 text-center' : 'ml-2'}>
              <Typography variant="small" className={`!text-xs sm:!text-sm ${state === 'active' ? '!text-[#1b2124]' : '!text-[#6b7280]'}`}>
                {step.description}
              </Typography>
            </div>
          </>;
      return <React.Fragment key={`step-${index}`}>
            {/* Step container */}
            {isClickable ? <button type="button" className={`flex ${isTextBottom ? 'flex-col items-center' : 'flex-row items-center'} cursor-pointer bg-transparent border-none p-0`} onClick={() => onClickStep?.(index)}>
                {stepContent}
              </button> : <div ref={index === currentStep ? activeRef : undefined} className={`flex ${isTextBottom ? 'flex-col items-center' : 'flex-row items-center'} ${state === 'active' ? 'cursor-default' : 'pointer-events-none cursor-not-allowed'}`} {...state === 'active' ? {
          'aria-current': 'step' as const
        } : {}}>
                {stepContent}
              </div>}

            {/* Connecting line */}
            {!isLast && (isVertical ? <div className={`flex ${isTextBottom ? 'justify-center' : 'ml-[11px] sm:ml-[13px]'}`}>
                  <div className="w-[1.5px] h-8 bg-[#d9dce1]" />
                </div> : <div className="flex items-center mx-1 mt-[12px] sm:mt-[14px] flex-1 min-w-[12px]">
                  <div className="h-[1.5px] w-full bg-[#d9dce1]" />
                </div>)}
          </React.Fragment>;
    })}
    </div>;
};
export default StepperComponenet;